import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AdminSidebar } from "@/pages/AdminUsers";
import { Eye, Building2 } from "lucide-react";

export default function AdminProposals() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/proposals");
        setProposals(data);
      } catch {
        toast.error("Failed to load proposals");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Administration
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">
            All proposals
          </h1>
          <p className="text-sm text-[#6C757D] mt-3">
            Every proposal generated by every agent in the network.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
          <AdminSidebar />
          <div>
            {loading ? (
              <div className="text-sm text-[#6C757D]">Loading…</div>
            ) : proposals.length === 0 ? (
              <Card className="border-dashed border-2 border-[#E9ECEF] bg-white p-12 text-center rounded-sm">
                <p className="text-sm text-[#6C757D]">No proposals yet.</p>
              </Card>
            ) : (
              <Card className="bg-white border border-[#E9ECEF] rounded-sm overflow-hidden" data-testid="admin-proposals-table-card">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#0B132B] text-white text-xs uppercase tracking-[0.14em]">
                        <th className="text-left px-4 py-3 font-semibold">Title</th>
                        <th className="text-left px-4 py-3 font-semibold">Agent</th>
                        <th className="text-left px-4 py-3 font-semibold">Dealership</th>
                        <th className="text-right px-4 py-3 font-semibold">Vehicles</th>
                        <th className="text-left px-4 py-3 font-semibold">Created</th>
                        <th className="px-3 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {proposals.map((p) => (
                        <tr key={p.id} className="border-b border-[#E9ECEF] hover:bg-[#F8F9FA]" data-testid={`admin-proposal-row-${p.id}`}>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-[#0B132B]">{p.title}</div>
                          </td>
                          <td className="px-4 py-3 text-[#6C757D]">
                            <div>{p.agent_name}</div>
                            <div className="text-xs">{p.agent_email}</div>
                          </td>
                          <td className="px-4 py-3 text-[#6C757D]">
                            {p.dealership ? (
                              <span className="inline-flex items-center gap-1.5">
                                <Building2 size={12} /> {p.dealership}
                              </span>
                            ) : "—"}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-[#0B132B]">
                            {p.vehicles?.length || 0}
                          </td>
                          <td className="px-4 py-3 text-xs text-[#6C757D]">
                            {new Date(p.created_at).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}
                          </td>
                          <td className="px-3 py-3">
                            <Link to={`/share/${p.share_token}`} target="_blank" data-testid={`admin-view-${p.id}`}>
                              <Button size="sm" variant="ghost" className="h-8 rounded-sm">
                                <Eye size={13} className="mr-1.5" /> View
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
